import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import ActionButton from "Components/atoms/buttons/action-button"
import { HelpIcon } from "Components/atoms/icons"
import SyncButton from "Components/molecule/sync-button"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import useManual from "hooks/useManual"
import useSyncProcess from "hooks/useSyncProcess"
import { SyncLogTypes } from "models/sync-log"
import { HumanResourcesSliceState, setShowSyncModal } from "slices/human-resources/reducer"
import { syncDayforceData } from "slices/human-resources/thunk"
import { SyncLogSliceState } from "slices/sync-log/reducer"
import { getMostRecentSyncLog } from "slices/thunk"

const selectHumanResources = createSelector(
    (state: any) => state.HumanResources,
    ({ showSyncModal }: HumanResourcesSliceState) => ({ showSyncModal })
)

const selectSyncLog = createSelector(
    (state: any) => state.SyncLog,
    ({ syncLogs }: SyncLogSliceState) => ({ syncLogs })
)

// Header buttons shared by the HR reports: Dayforce Sync (with its confirm dialog) and Help.
// onSyncFinished reloads the report once the sync is done (useSyncProcess polls the sync log).
const HrReportActions = ({ manual, onSyncFinished }: { manual: string, onSyncFinished?: () => void | Promise<void> }) => {
    const dispatch: any = useDispatch()
    const { handleOpenManual } = useManual(manual)
    const { showSyncModal } = useSelector(selectHumanResources)
    const { isSyncing: isSyncingData, startSync } = useSyncProcess({
        module: SyncLogTypes.EMPLOYEE_DAYFORCE,
        start: syncDayforceData,
        onFinished: onSyncFinished,
    })
    const { syncLogs } = useSelector(selectSyncLog)

    useEffect(() => {
        if (!syncLogs[SyncLogTypes.EMPLOYEE_DAYFORCE])
            dispatch(getMostRecentSyncLog(SyncLogTypes.EMPLOYEE_DAYFORCE))
    }, [])

    const closeModal = () => dispatch(setShowSyncModal({ show: false }))

    const handleSyncData = async () => {
        closeModal()
        await startSync()
    }

    return <>
        <SyncButton
            id="hrReportSyncBtn"
            isSyncing={isSyncingData}
            syncLog={syncLogs[SyncLogTypes.EMPLOYEE_DAYFORCE]}
            onClick={() => dispatch(setShowSyncModal({ show: true }))}
        />
        <ActionButton onClick={handleOpenManual}>
            Help <HelpIcon />
        </ActionButton>
        <AreYouSureModal
            isOpen={showSyncModal}
            onModalClose={closeModal}
            modalTitle={"Are you sure you want to sync?"}
            handleAreYouSureAccept={handleSyncData}
            handleAreYouSureDecline={closeModal}
            areYouSureacceptText={isSyncingData ? "Syncing..." : "Sync Data"}
            areYouSuredeclineText={"Cancel"}
        />
    </>
}

export default HrReportActions
